import React, { createContext, useContext, useState } from 'react';
import { Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';

// Sidebar Context - open is for mobile menu, collapsed is for desktop icon-only mode
const SidebarContext = createContext({
  open: true,
  setOpen: () => {},
  collapsed: false,
  setCollapsed: () => {},
});

export function SidebarProvider({ children, defaultOpen = true, defaultCollapsed = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <SidebarContext.Provider value={{ open, setOpen, collapsed, setCollapsed }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return context;
}

// Sidebar Component
export function Sidebar({ children, className, style, ...props }) {
  const { open, collapsed } = useSidebar();

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen transition-all duration-300 flex flex-col',
        collapsed ? 'w-[72px]' : 'w-64',
        open ? 'translate-x-0' : '-translate-x-full',
        'md:translate-x-0',
        className
      )}
      style={style}
      {...props}
    >
      {children}
    </aside>
  );
}

// Sidebar Collapse Toggle Button
export function SidebarCollapseButton({ className, ...props }) {
  const { collapsed, setCollapsed } = useSidebar();

  return (
    <button
      className={cn(
        'absolute -right-3 top-20 z-50 hidden h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm transition-colors hover:bg-slate-50 md:flex',
        className
      )}
      onClick={() => setCollapsed(!collapsed)}
      {...props}
    >
      {collapsed ? (
        <ChevronRight className="w-4 h-4 text-slate-600" />
      ) : (
        <ChevronLeft className="w-4 h-4 text-slate-600" />
      )}
    </button>
  );
}

// Sidebar Header
export function SidebarHeader({ children, className, ...props }) {
  return (
    <div className={cn('flex items-center px-4', className)} {...props}>
      {children}
    </div>
  );
}

// Sidebar Content
export function SidebarContent({ children, className, ...props }) {
  return (
    <div className={cn('flex-1 overflow-y-auto', className)} {...props}>
      {children}
    </div>
  );
}

// Sidebar Footer
export function SidebarFooter({ children, className, ...props }) {
  return (
    <div className={cn('border-t p-3', className)} {...props}>
      {children}
    </div>
  );
}

// Sidebar Group
export function SidebarGroup({ children, className, ...props }) {
  return (
    <div className={cn('px-2 py-1', className)} {...props}>
      {children}
    </div>
  );
}

// Sidebar Group Label
export function SidebarGroupLabel({ children, className, ...props }) {
  const { collapsed } = useSidebar();
  
  if (collapsed) {
    return <div className="h-2" />; // Small spacer when collapsed
  }
  
  return (
    <div className={cn('px-2 py-1 text-xs font-semibold', className)} {...props}>
      {children}
    </div>
  );
}

// Sidebar Group Content
export function SidebarGroupContent({ children, className, ...props }) {
  return (
    <div className={cn('space-y-1', className)} {...props}>
      {children}
    </div>
  );
}

// Sidebar Menu
export function SidebarMenu({ children, className, ...props }) {
  return (
    <ul className={cn('space-y-1', className)} {...props}>
      {children}
    </ul>
  );
}

// Sidebar Menu Item
export function SidebarMenuItem({ children, className, ...props }) {
  return (
    <li className={cn('', className)} {...props}>
      {children}
    </li>
  );
}

// Sidebar Menu Button
export function SidebarMenuButton({ asChild = false, children, className, ...props }) {
  const { collapsed } = useSidebar();
  
  const baseClasses = collapsed 
    ? 'flex items-center justify-center px-3 py-2.5 w-full'
    : 'flex items-center gap-3 px-3 py-2.5 w-full';
  
  if (asChild) {
    // Merge child's className with the passed className
    const childClassName = children.props?.className || '';
    return React.cloneElement(children, {
      className: cn(baseClasses, childClassName, className),
      ...props,
    });
  }

  return (
    <button className={cn(baseClasses, 'text-left', className)} {...props}>
      {children}
    </button>
  );
}

// Sidebar Trigger
export function SidebarTrigger({ className, ...props }) {
  const { open, setOpen } = useSidebar();

  return (
    <button
      className={cn('p-2 rounded-lg', className)}
      onClick={() => setOpen(!open)}
      {...props}
    >
      {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
    </button>
  );
}

